// Coach client quick find. One search box, two entry points:
//   - inline on the Command dashboard header (results drop down under it)
//   - the quick-find dialog CoachLayout opens with Cmd+K, Ctrl+K, "/", or
//     the search button in the phone top bar (full height on phone).
// Results match name or email (server side, coach scoped) and open the
// client's profile. Arrow keys move, Enter opens, Esc closes.
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Search, X } from 'lucide-react';
import { useLocation } from 'wouter';
import { trpc } from '@/lib/trpc';
import { isDirectoryShortcut, isEditableTarget } from '@/components/layout/directoryShortcut';
import '@/styles/client-quick-find.css';

const DEBOUNCE_MS = 180;
const RESULT_LIMIT = 8;

type Result = {
  id: number;
  name: string;
  email: string | null;
  avatarUrl: string | null;
  status: 'active' | 'paused' | 'alumni' | null;
};

const STATUS_LABEL: Record<NonNullable<Result['status']>, string> = {
  active: 'Active',
  paused: 'Paused',
  alumni: 'Alumni',
};

function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).map(part => part[0]).join('').slice(0, 2).toUpperCase() || '?';
}

function useDebounced<T>(value: T, ms: number) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

export function ClientSearchBox({
  variant,
  autoFocus = false,
  onDone,
  className = '',
}: {
  variant: 'inline' | 'dialog';
  autoFocus?: boolean;
  /** Called after a result opens (the dialog closes itself here). */
  onDone?: () => void;
  className?: string;
}) {
  const [, navigate] = useLocation();
  const [text, setText] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const baseId = useId();
  const listId = `${baseId}-results`;
  const optionId = (index: number) => `${baseId}-option-${index}`;

  const q = text.trim();
  const debounced = useDebounced(q, DEBOUNCE_MS);
  const search = trpc.clients.search.useQuery(
    { q: debounced, limit: RESULT_LIMIT },
    { enabled: debounced.length > 0, staleTime: 30_000, placeholderData: previous => previous },
  );
  const results: Result[] = q && debounced ? (search.data ?? []) : [];
  const settled = q === debounced && !search.isFetching;
  const showList = (variant === 'dialog' || listOpen) && q.length > 0;

  useEffect(() => { setActive(0); }, [debounced]);
  useEffect(() => { if (autoFocus) inputRef.current?.focus(); }, [autoFocus]);
  useEffect(() => {
    if (!showList) return;
    const el = document.getElementById(optionId(active));
    el?.scrollIntoView?.({ block: 'nearest' });
    // optionId is derived from baseId, which never changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, showList]);

  const open = (result: Result | undefined) => {
    if (!result) return;
    setText('');
    setListOpen(false);
    inputRef.current?.blur();
    onDone?.();
    navigate(`/coach/clients/${result.id}`);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setListOpen(true);
      if (!results.length) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive(index => (index + step + results.length) % results.length);
    } else if (event.key === 'Enter') {
      if (!showList || !results.length) return;
      event.preventDefault();
      open(results[Math.min(active, results.length - 1)]);
    } else if (event.key === 'Escape' && variant === 'inline') {
      event.preventDefault();
      if (listOpen && q) setListOpen(false);
      else { setText(''); inputRef.current?.blur(); }
    }
  };

  let body: ReactNode = null;
  if (showList) {
    if (results.length) {
      body = (
        <ul id={listId} role="listbox" aria-label="Clients" className="cqf-list">
          {results.map((result, index) => (
            <li
              key={result.id}
              id={optionId(index)}
              role="option"
              aria-selected={index === active}
              className="cqf-row"
              data-active={index === active || undefined}
              onMouseDown={event => event.preventDefault()}
              onMouseMove={() => { if (index !== active) setActive(index); }}
              onClick={() => open(result)}
            >
              <span className="cqf-av" aria-hidden="true">
                {result.avatarUrl ? <img src={result.avatarUrl} alt="" /> : initials(result.name)}
              </span>
              <span className="cqf-row__text">
                <span className="cqf-row__name">{result.name}</span>
                {result.email ? <span className="cqf-row__email">{result.email}</span> : null}
              </span>
              {result.status ? (
                <span className="cqf-status" data-status={result.status}>{STATUS_LABEL[result.status]}</span>
              ) : null}
            </li>
          ))}
        </ul>
      );
    } else {
      body = (
        <p className="cqf-empty" role="status">
          {search.isError ? 'Search is not available right now. Try again.' : settled ? 'No clients match' : 'Searching...'}
        </p>
      );
    }
  }

  return (
    <div className={`cqf-box cqf-box--${variant} ${className}`.trim()} data-testid={`client-search-${variant}`}>
      <div className="cqf-field">
        <Search className="cqf-field__icon" aria-hidden="true" />
        <input
          ref={inputRef}
          type="search"
          inputMode="search"
          enterKeyHint="go"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          role="combobox"
          aria-label="Search clients by name or email"
          aria-autocomplete="list"
          aria-expanded={showList}
          aria-controls={showList && results.length ? listId : undefined}
          aria-activedescendant={showList && results.length ? optionId(Math.min(active, results.length - 1)) : undefined}
          placeholder="Search clients by name or email"
          value={text}
          onChange={event => { setText(event.target.value); setListOpen(true); }}
          onFocus={() => setListOpen(true)}
          onBlur={() => setListOpen(false)}
          onKeyDown={onKeyDown}
        />
        {text ? (
          <button
            type="button"
            className="cqf-field__clear"
            aria-label="Clear search"
            onMouseDown={event => event.preventDefault()}
            onClick={() => { setText(''); inputRef.current?.focus(); }}
          >
            <X aria-hidden="true" />
          </button>
        ) : null}
      </div>
      {body ? <div className="cqf-results">{body}</div> : null}
    </div>
  );
}

export function ClientQuickFindDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="cqf-overlay" />
        <DialogPrimitive.Content className="cqf-dialog" aria-describedby={undefined} data-testid="client-quick-find">
          <div className="cqf-dialog__head">
            <DialogPrimitive.Title className="cqf-dialog__title">Find a client</DialogPrimitive.Title>
            <DialogPrimitive.Close className="cqf-dialog__close">Close</DialogPrimitive.Close>
          </div>
          <ClientSearchBox variant="dialog" autoFocus onDone={() => onOpenChange(false)} />
          <p className="cqf-dialog__hint" aria-hidden="true">
            <span><kbd>↑</kbd><kbd>↓</kbd> move</span>
            <span><kbd>Enter</kbd> open</span>
            <span><kbd>Esc</kbd> close</span>
          </p>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/** Keyboard entry for the quick find: Cmd+K or Ctrl+K anywhere, or "/" when
 * nothing editable has focus. Ignored while another dialog is open. */
export function useClientQuickFindShortcut(
  setOpen: (update: (open: boolean) => boolean) => void,
  enabled = true,
) {
  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing) return;
      const focus = document.activeElement;
      const inOtherDialog = [event.target, focus].some(node =>
        node instanceof Element && node.closest('[role="dialog"], [role="alertdialog"], dialog[open]')
        && !node.closest('.cqf-dialog'));
      if (inOtherDialog) return;
      if (isDirectoryShortcut(event)) {
        event.preventDefault();
        setOpen(open => !open);
        return;
      }
      if (event.key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey
        && !isEditableTarget(event.target) && !isEditableTarget(focus)) {
        event.preventDefault();
        setOpen(() => true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [setOpen, enabled]);
}
